import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router";
import { fetchContent } from "../../api/content";
import {
  completeFinanceStep,
  fetchFinance,
  hygieneAnswers,
  saveFinanceChecklist,
  saveFinanceServices,
  saveHygieneAnswers,
  setLmsDone,
  type FinanceView,
} from "../../api/finance";
import { fetchPathway } from "../../api/pathway";
import { Banner } from "../../components/Banner";
import { ScreenState } from "../../components/ScreenState";
import { attempt } from "../../lib/errors";
import { useAsync } from "../../lib/useAsync";
import {
  FinChecklistScreen,
  FinHandoverScreen,
  FinKeepGoingScreen,
  FinReadinessScreen,
  FinServicesScreen,
} from "../../screens/finance/FinanceScreens";
import { DoneScreen } from "./DoneScreen";

const DONE_COPY: Record<
  number,
  { title: string; sub: string; next?: { to: string; label: string } }
> = {
  1: {
    title: "You're in the finance track.",
    sub: "Next, get your ID documents ready so your provider can verify you quickly when you're referred.",
    next: { to: "/fin/2", label: "Start Finance Step 2" },
  },
  2: {
    title: "Your ID documents are ready.",
    sub: "Next, gather the money records providers use for their checks.",
    next: { to: "/fin/3", label: "Start Finance Step 3" },
  },
  3: {
    title: "Your records are ready.",
    sub: "Next, a quick readiness check so you know where you stand before your provider's own checks.",
    next: { to: "/fin/4", label: "Start Finance Step 4" },
  },
  4: {
    title: "Your readiness is known.",
    sub: "Keep going on the pathway. You're referred to your provider when you complete it, so the finish line is worth reaching.",
  },
  5: {
    title: "Your handover is open.",
    sub: "You've completed the Growth Pathway. Open Finance Step 6 to be referred to your provider.",
    next: { to: "/fin/6", label: "Open Finance Step 6" },
  },
};

export function FinanceStepRoute() {
  const step = Number(useParams().step);
  const loaded = useAsync(
    () => Promise.all([fetchFinance(), fetchContent(), fetchPathway()]),
    [step],
  );
  const [view, setView] = useState<FinanceView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState<number | null>(null);

  useEffect(() => {
    if (loaded.data) {
      setView(loaded.data[0]);
      setFinished(null);
    }
  }, [loaded.data]);

  if (!(step >= 1 && step <= 6)) return <Navigate to="/hub" replace />;

  const status = loaded.data?.[2].finance[step - 1];
  if (status?.state === "locked") return <Navigate to="/hub" replace />;

  if (finished && DONE_COPY[finished]) {
    const copy = DONE_COPY[finished];
    return (
      <DoneScreen
        kicker={`Finance Step ${finished} complete`}
        title={copy.title}
        sub={copy.sub}
        {...(copy.next ? { next: copy.next } : {})}
      />
    );
  }

  const save = (work: () => Promise<FinanceView>) =>
    void attempt(
      () => undefined,
      setError,
      async () => setView(await work()),
    );
  const complete = () =>
    void attempt(setBusy, setError, async () => {
      const wasDone = status?.state === "done";
      setView(await completeFinanceStep(step));
      if (!wasDone && step < 6) setFinished(step);
    });

  let body: React.ReactNode = null;
  if (view && loaded.data) {
    const [, content, pathway] = loaded.data;
    const common = { view, busy, done: status?.state === "done", onComplete: complete };
    if (step === 1)
      body = (
        <FinServicesScreen
          {...common}
          services={content.finServices}
          onChange={(keys) => {
            setView({ ...view, record: { ...view.record, services: keys } });
            save(() => saveFinanceServices(keys));
          }}
        />
      );
    else if (step === 2 || step === 3) {
      const list = step === 2 ? "idDocs" : "moneyDocs";
      body = (
        <FinChecklistScreen
          {...common}
          step={step}
          items={step === 2 ? content.idDocs : content.moneyDocs}
          selected={view.record[list]}
          onChange={(keys) => {
            setView({ ...view, record: { ...view.record, [list]: keys } });
            save(() => saveFinanceChecklist(list, keys));
          }}
        />
      );
    } else if (step === 4)
      body = (
        <FinReadinessScreen
          {...common}
          questions={content.hygiene}
          onAnswer={(key, yes) => {
            const answers = { ...hygieneAnswers(view), [key]: yes };
            const hygiene = Object.entries(answers).map(([k, v]) => ({ key: k, yes: v }));
            setView({ ...view, record: { ...view.record, hygieneAnswers: hygiene } });
            save(() => saveHygieneAnswers([{ key, yes }]));
          }}
        />
      );
    else if (step === 5)
      body = (
        <FinKeepGoingScreen
          {...common}
          gigStep5Done={pathway.gig[4]?.state === "done"}
          gigStep5Open={pathway.gig[4]?.state !== "locked"}
          onLms={(done) => save(() => setLmsDone(done))}
        />
      );
    else body = <FinHandoverScreen {...common} />;
  }

  return (
    <ScreenState loading={loaded.loading} error={loaded.error} onRetry={loaded.reload}>
      {error ? <Banner tone="bad">{error}</Banner> : null}
      {body}
    </ScreenState>
  );
}
